import { Component, OnInit, OnDestroy, ViewChild, ElementRef, Input, Output, EventEmitter } from '@angular/core';
import { CommonModule } from '@angular/common';
import { DialogModule } from 'primeng/dialog';
import { ButtonModule } from 'primeng/button';
import { TooltipModule } from 'primeng/tooltip';
import { MessageService } from 'primeng/api';
import { NGXLogger } from 'ngx-logger';
import * as maplibregl from 'maplibre-gl';
import { SchedeService } from '../../../services/schede.service';
import { ComuniService } from '../../../services/comuni.service';
import { SchedaGeoJsonUtils, SchedeFeatureCollection } from '../../../models/scheda-geojson.model';
import { ComuniFeatureCollection } from '../../../models/comune-geojson.model';
import proj4 from 'proj4';
import { Scheda } from '../../../models/scheda.model';

@Component({
  selector: 'app-mappa-scheda',
  standalone: true,
  imports: [
    CommonModule,
    DialogModule,
    ButtonModule,
    TooltipModule
  ],
  templateUrl: './mappa-scheda.component.html',
  styleUrl: './mappa-scheda.component.scss'
})
export class MappaSchedaComponent implements OnInit, OnDestroy {
  @ViewChild('map') mapContainer!: ElementRef;
  @Input() visible: boolean = false;
  @Input() titolo: string = 'Mappa dettagliata';
  @Input() datiGeoJSON: any = null;
  @Input() scheda: Scheda | null = null;
  @Output() visibleChange = new EventEmitter<boolean>();

  private map: maplibregl.Map | null = null;
  private markers: maplibregl.Marker[] = [];
  private comuniData?: ComuniFeatureCollection;

  // Opzioni mappa
  opzioniMappa = {
    zoomScheda: true,
    mostraComuni: true,
    mostraPoints: true,
    mostraLines: true,
    mostraPolygons: true
  };

  constructor(
    private logger: NGXLogger,
    private messageService: MessageService,
    private schedeService: SchedeService,
    private comuniService: ComuniService
  ) {}

  ngOnInit(): void {
    this.caricaConfiniComuni();
  }

  onShow(): void {
    setTimeout(() => {
      this.initializeMap();
    }, 100);
  }

  onHide(): void {
    this.visibleChange.emit(false);
    if (this.map) {
      this.map.remove();
      this.map = null;
    }
  }

  /**
   * Carica i dati dei confini dei comuni
   */
  private caricaConfiniComuni(): void {
    this.comuniService.getConfiniComuni().subscribe({
      next: (data) => {
        this.comuniData = data;
        // this.logger.info('[MAPPA-SCHEDA] Confini comuni caricati con successo');
        if (this.map && this.opzioniMappa.mostraComuni) {
          this.aggiungiConfiniComuni();
        }
      },
      error: (error) => {
        this.logger.error('[MAPPA-SCHEDA] Errore nel caricamento dei confini comuni', error);
      }
    });
  }

  /**
   * Inizializza la mappa nella modale
   */
  private initializeMap(): void {
    if (!this.mapContainer || !this.mapContainer.nativeElement) {
      this.logger.error('[MAPPA-SCHEDA] Elemento mappa non trovato nel DOM');
      return;
    }

    try {
      // Configurazione iniziale della mappa - Valle di Susa
      const initialState = {
        lng: 7.200,    // Longitudine Valle di Susa
        lat: 45.1381,  // Latitudine Valle di Susa (Susa)
        zoom: 9.8      // Livello di zoom per vedere tutta la valle
      };

      // Creazione della mappa
      this.map = new maplibregl.Map({
        container: this.mapContainer.nativeElement,
        style: {
          version: 8 as 8,
          sources: {
            'osm-tiles': {
              type: 'raster',
              tiles: ['https://tile.openstreetmap.org/{z}/{x}/{y}.png'],
              tileSize: 256
            }
          },
          layers: [
            {
              id: 'osm-tiles',
              type: 'raster',
              source: 'osm-tiles',
              minzoom: 0,
              maxzoom: 19
            }
          ]
        },
        center: [initialState.lng, initialState.lat],
        zoom: initialState.zoom
      });

      // Aggiungi controlli alla mappa
      (this.map as any).addControl(new maplibregl.NavigationControl(), 'top-right');
      (this.map as any).addControl(new maplibregl.ScaleControl(), 'bottom-left');

      // Quando la mappa è caricata, aggiungi dati
      this.map.on('load', () => {
        // this.logger.info('[MAPPA-SCHEDA] Mappa caricata con successo');

        // Aggiungi i confini dei comuni
        if (this.opzioniMappa.mostraComuni && this.comuniData) {
          this.aggiungiConfiniComuni();
        }

        // Carica le geometrie delle schede
        if (this.datiGeoJSON) {
          // Usa i dati forniti come input
          this.caricaGeometriePersonalizzate(this.datiGeoJSON);
        } else {
          // Carica tutte le geometrie
          this.caricaGeometrieSchede();
        }
      });
    } catch (error) {
      this.logger.error('[MAPPA-SCHEDA] Errore durante l\'inizializzazione della mappa:', error);
    }
  }

  /**
   * Aggiunge i confini dei comuni alla mappa
   */
  private aggiungiConfiniComuni(): void {
    if (!this.map || !this.comuniData) {
      this.logger.warn('[MAPPA-SCHEDA] Impossibile aggiungere confini comuni: mappa o dati non disponibili');
      return;
    }

    try {
      // Verifica se la source esiste già
      if (!(this.map as any).getSource('comuni-source')) {
         this.logger.info('[MAPPA-SCHEDA] Aggiunta dei confini dei comuni');

        // Aggiungi la fonte dei dati GeoJSON alla mappa
        (this.map as any).addSource('comuni-source', {
          type: 'geojson',
          data: this.comuniData
        });
        console.log('comuni-source', this.comuniData);

        // Aggiungi SOLO il layer per i confini dei comuni (senza riempimento)
        (this.map as any).addLayer({
          id: 'comuni-confini',
          type: 'line',
          source: 'comuni-source',
          layout: {},
          paint: {
            'line-color': '#FF0000',  // Rosso ben visibile
            'line-width': 2.5,        // Spessore linea
            'line-opacity': 1         // Opacità
          }
        });

        // this.logger.info('[MAPPA-SCHEDA] Confini comuni aggiunti');
      }
    } catch (err) {
      this.logger.error('[MAPPA-SCHEDA] Errore nell\'aggiunta dei confini comuni', err);
    }
  }

  /**
   * Carica tutte le geometrie delle schede
   */
  private caricaGeometrieSchede(): void {
    this.schedeService.getGeometrieSchede().subscribe(
      geometrie => {
        // this.logger.info('[MAPPA-SCHEDA] ' + (geometrie.features?.length || 0) + ' Geometrie schede recuperate con successo');

        try {
          // Configura proj4 per la conversione di coordinate
          proj4.defs('EPSG:32632', '+proj=utm +zone=32 +datum=WGS84 +units=m +no_defs');

          // Converti le coordinate in tutte le geometrie
          if (geometrie.features && geometrie.features.length > 0) {
            geometrie.features.forEach(feature => {
              if (feature.geometry) {
                this.convertCoordinates(feature.geometry);
              }
            });
          }

          // Processa e visualizza i dati
          this.elaboraEVisualizzaDati(geometrie);
        } catch (error) {
          this.logger.error('[MAPPA-SCHEDA] Errore nella conversione dei dati GeoJSON:', error);
          this.messageService.add({
            severity: 'error',
            summary: 'Errore dati',
            detail: 'Errore durante l\'elaborazione delle geometrie',
            life: 5000
          });
        }
      },
      error => {
        this.logger.error('[MAPPA-SCHEDA] Errore nel recupero delle geometrie delle schede:', error);
        this.messageService.add({
          severity: 'error',
          summary: 'Errore di connessione',
          detail: 'Impossibile recuperare le geometrie delle schede',
          life: 5000
        });
      }
    );
  }

  /**
   * Carica geometrie personalizzate dal datiGeoJSON fornito come input
   */
  private caricaGeometriePersonalizzate(geoJSON: any): void {
    try {
      // Configura proj4 per la conversione di coordinate
      proj4.defs('EPSG:32632', '+proj=utm +zone=32 +datum=WGS84 +units=m +no_defs');

      // Converti le coordinate se necessario
      if (geoJSON.features && geoJSON.features.length > 0) {
        geoJSON.features.forEach((feature: any) => {
          if (feature.geometry) {
            this.convertCoordinates(feature.geometry);
          }
        });
      }

      // Processa e visualizza i dati
      this.elaboraEVisualizzaDati(geoJSON);

      // Se richiesto, fai zoom sulla geometria
      if (this.opzioniMappa.zoomScheda) {
        this.zoomSuGeometrie(geoJSON);
      }
    } catch (error) {
      this.logger.error('[MAPPA-SCHEDA] Errore nell\'elaborazione delle geometrie personalizzate:', error);
    }
  }

  /**
   * Elabora e visualizza i dati GeoJSON sulla mappa
   */
  private elaboraEVisualizzaDati(geometrie: any): void {
    // Converti in una FeatureCollection valida
    const geoJsonFeatureCollection = SchedaGeoJsonUtils.toFeatureCollection(geometrie);

    // Separa le feature in base al tipo geometriaMarker e al tipo effettivo della geometria
    const pointFeatures: SchedeFeatureCollection = {
      type: 'FeatureCollection',
      features: geoJsonFeatureCollection.features.filter(f =>
        (f.properties as any).geometriaMarker === 'point' ||
        f.geometry.type === 'Point' ||
        f.geometry.type === 'MultiPoint')
    };

    const lineFeatures: SchedeFeatureCollection = {
      type: 'FeatureCollection',
      features: geoJsonFeatureCollection.features.filter(f =>
        (f.properties as any).geometriaMarker === 'linestring' ||
        f.geometry.type === 'LineString' ||
        f.geometry.type === 'MultiLineString')
    };

    const polygonFeatures: SchedeFeatureCollection = {
      type: 'FeatureCollection',
      features: geoJsonFeatureCollection.features.filter(f =>
        (f.properties as any).geometriaMarker === 'polygon' ||
        f.geometry.type === 'Polygon' ||
        f.geometry.type === 'MultiPolygon')
    };

    // Standardizza le geometrie in base al loro tipo
    const standardizedPoints = pointFeatures.features.length > 0 ?
      SchedaGeoJsonUtils.standardizzaGeometrie(pointFeatures, 'point') : null;

    const standardizedLines = lineFeatures.features.length > 0 ?
      SchedaGeoJsonUtils.standardizzaGeometrie(lineFeatures, 'line') : null;

    const standardizedPolygons = polygonFeatures.features.length > 0 ?
      SchedaGeoJsonUtils.standardizzaGeometrie(polygonFeatures, 'polygon') : null;

    // Assegna i colori a tutte le geometrie
    [standardizedPoints, standardizedLines, standardizedPolygons]
      .filter(collection => collection !== null)
      .forEach(collection => {
        // Asserzione di non-nullità per risolvere l'errore S18047
        (collection!).features.forEach(feature => {
          (feature.properties as any).color = feature.properties.geometriaColore;
        });
      });

    // Aggiungi le geometrie alla mappa se presenti
    if (this.map) {
      // Gestisci i point layer (se abilitati)
      if (this.opzioniMappa.mostraPoints && standardizedPoints && standardizedPoints.features.length > 0) {
        this.aggiungiPointLayers(standardizedPoints);
      }

      // Gestisci i line layer (se abilitati)
      if (this.opzioniMappa.mostraLines && standardizedLines && standardizedLines.features.length > 0) {
        this.aggiungiLineLayers(standardizedLines);
      }

      // Gestisci i polygon layer (se abilitati)
      if (this.opzioniMappa.mostraPolygons && standardizedPolygons && standardizedPolygons.features.length > 0) {
        this.aggiungiPolygonLayers(standardizedPolygons);
      }

      // Aggiungi interattività ai vari layer
      this.aggiungiInterattivitaGeometrie();
    }
  }

  /**
   * Aggiungi i layer per i punti
   */
  private aggiungiPointLayers(standardizedPoints: SchedeFeatureCollection): void {
    if (!this.map) return;

    // Crea immagini per i marker speciali
    this.creaTriangoloMarker();
    this.creaAsteriskMarker();

    (this.map as any).addSource('points-source', {
      type: 'geojson',
      data: standardizedPoints
    });

    // Layer per i marker circolari (default)
    (this.map as any).addLayer({
      id: 'points-circle-source',
      type: 'circle',
      source: 'points-source',
      filter: [
        'all',
        ['!=', ['get', 'color'], '#33a02c'],
        ['!=', ['get', 'oggettoInterventoCodice'], 'SG']
      ],
      paint: {
        'circle-radius': 6,
        'circle-color': ['get', 'color'],
        'circle-stroke-width': 2,
        'circle-stroke-color': '#ffffff'
      }
    });

    // Layer per i marker triangolari (per punti verdi)
    (this.map as any).addLayer({
      id: 'points-triangle-source',
      type: 'symbol',
      source: 'points-source',
      filter: [
        'all',
        ['==', ['get', 'color'], '#33a02c'],
        ['!=', ['get', 'oggettoInterventoCodice'], 'SG']
      ],
      layout: {
        'icon-image': 'triangle-marker',
        'icon-size': 0.7,
        'icon-allow-overlap': true,
      }
    });

    // Layer per i marker a forma di asterisco (per oggettoInterventoCodice = SG)
    (this.map as any).addLayer({
      id: 'points-star-source',
      type: 'symbol',
      source: 'points-source',
      filter: ['==', ['get', 'oggettoInterventoCodice'], 'SG'],
      layout: {
        'icon-image': 'asterisk-marker',
        'icon-size': 0.7,
        'icon-allow-overlap': true,
      },
      paint: {
        'icon-color': ['get', 'color'] // Usa il colore della feature
      }
    });
  }

  /**
   * Aggiungi i layer per le linee
   */
  private aggiungiLineLayers(standardizedLines: SchedeFeatureCollection): void {
    if (!this.map) return;

    (this.map as any).addSource('lines-source', {
      type: 'geojson',
      data: standardizedLines
    });

    // Layer per le linestring normali (solide)
    (this.map as any).addLayer({
      id: 'lines-source',
      type: 'line',
      source: 'lines-source',
      filter: [
        'all',
        ['!=', ['get', 'oggettoInterventoCodice'], 'AV'],
        ['!=', ['get', 'oggettoInterventoCodice'], 'AL']
      ],
      layout: {
        'line-join': 'round',
        'line-cap': 'round'
      },
      paint: {
        'line-color': ['get', 'color'],
        'line-width': 3,
        'line-opacity': 1
      }
    });

    // Layer per le linestring tratteggiate (AV o AL)
    (this.map as any).addLayer({
      id: 'lines-dashed-source',
      type: 'line',
      source: 'lines-source',
      filter: [
        'any',
        ['==', ['get', 'oggettoInterventoCodice'], 'AV'],
        ['==', ['get', 'oggettoInterventoCodice'], 'AL']
      ],
      layout: {
        'line-join': 'round',
        'line-cap': 'round'
      },
      paint: {
        'line-color': ['get', 'color'],
        'line-width': 3,
        'line-opacity': 1,
        'line-dasharray': [3, 2] // Pattern tratteggiato: 3px linea, 2px spazio
      }
    });
  }

  /**
   * Aggiungi i layer per i poligoni
   */
  private aggiungiPolygonLayers(standardizedPolygons: SchedeFeatureCollection): void {
    if (!this.map) return;

    // Creo un canvas per generare il pattern di linee parallele
    this.creaPatternLineeDiagonali();

    (this.map as any).addSource('polygons-source', {
      type: 'geojson',
      data: standardizedPolygons
    });

    // Aggiungi il contorno dei poligoni
    (this.map as any).addLayer({
      id: 'polygons-outline-source',
      type: 'line',
      source: 'polygons-source',
      paint: {
        'line-color': ['get', 'color'],
        'line-width': 2,
        'line-opacity': 1
      }
    });

    // Aggiungi il riempimento dei poligoni con pattern a linee
    (this.map as any).addLayer({
      id: 'polygons-source',
      type: 'fill',
      source: 'polygons-source',
      paint: {
        'fill-color': ['get', 'color'],
        'fill-opacity': 0.8,  // Riduco l'opacità
        'fill-pattern': 'diagonal-stripes'  // Uso il pattern creato
      }
    });
  }

  /**
   * Aggiunge interattività alle geometrie: tooltip al passaggio del mouse e popup al click
   */
  private aggiungiInterattivitaGeometrie(): void {
    if (!this.map) return;

    // Crea un elemento div per il tooltip
    const tooltipEl = document.createElement('div');
    tooltipEl.className = 'maplibre-tooltip';
    tooltipEl.style.display = 'none';
    tooltipEl.style.position = 'absolute';
    tooltipEl.style.backgroundColor = 'white';
    tooltipEl.style.padding = '6px 10px';
    tooltipEl.style.borderRadius = '4px';
    tooltipEl.style.boxShadow = '0 1px 4px rgba(0,0,0,0.3)';
    tooltipEl.style.fontSize = '12px';
    tooltipEl.style.pointerEvents = 'none'; // Consente l'interazione con ciò che c'è sotto
    tooltipEl.style.zIndex = '1000';
    tooltipEl.innerHTML = 'Tooltip';

    // Aggiungi il tooltip al container della mappa
    const mapContainer = (this.map as any).getContainer();
    mapContainer.appendChild(tooltipEl);

    // Layer IDs da monitorare per l'interattività
    const layerIds = ['points-circle-source', 'points-triangle-source', 'points-star-source', 'lines-source', 'lines-dashed-source', 'polygons-source', 'polygons-outline-source'];

    // Gestisci il mousemove per mostrare il tooltip
    layerIds.forEach(layerId => {
      (this.map as any).on('mousemove', layerId, (e: any) => {
        if (!this.map || !e.features || e.features.length === 0) return;

        // Imposta il cursore a pointer
        (this.map as any).getCanvas().style.cursor = 'pointer';

        // Estrai informazioni dalla feature
        const feature = e.features[0];
        const titolo = feature.properties.titolo || 'Scheda senza titolo';
        const codice = feature.properties.codice || '';
        const vecchioCodiceUmvs = feature.properties.vecchioCodiceUmvs || '';
        const color = feature.properties.color || '#000000';

        // Crea il contenuto del tooltip con i due codici affiancati
        const tooltipContent = `
          <div style="display: flex; align-items: center; gap: 0.5rem;">
            ${vecchioCodiceUmvs ? `
              <div style="display: flex; align-items: center; background: #f0f0f0; padding: 0.25rem 0.5rem; border-radius: 4px; border-left: 3px solid #8f5cb7; min-width: 60px;">
                <span style="color: #8B4513; font-weight: bold; font-size: 11px;">${vecchioCodiceUmvs}</span>
              </div>
            ` : ''}
            <div style="display: flex; align-items: center; background: #f0f0f0; padding: 0.25rem 0.5rem; border-radius: 4px; border-left: 3px solid #4285F4; min-width: 60px;">
              <span style="color: #3a6e75; font-weight: bold; font-size: 11px;">${codice}</span>
            </div>
          </div>
        `;

        // Aggiorna il contenuto del tooltip
        tooltipEl.innerHTML = tooltipContent;

        // Posiziona il tooltip vicino al cursore
        tooltipEl.style.left = `${e.point.x + 10}px`;
        tooltipEl.style.top = `${e.point.y - 20}px`;

        // Mostra il tooltip
        tooltipEl.style.display = 'block';
      });

      // Nascondi il tooltip quando il mouse esce dalla geometria
      (this.map as any).on('mouseleave', layerId, () => {
        if (!this.map) return;

        // Ripristina il cursore
        (this.map as any).getCanvas().style.cursor = '';

        // Nascondi il tooltip
        tooltipEl.style.display = 'none';
      });

      // Gestisci il click per mostrare il popup
      (this.map as any).on('click', layerId, (e: any) => {
        if (!this.map || !e.features || e.features.length === 0) return;

        // Estrai informazioni dalla feature
        const feature = e.features[0];
        // this.logger.info('[MAPPA-SCHEDA] Feature:', feature);
        const titolo = feature.properties.titolo || 'Scheda senza titolo';
        const codice = feature.properties.codice || '';
        const vecchioCodiceUmvs = feature.properties.vecchioCodiceUmvs || '';
        const codiceATO = feature.properties.codiceAto || '';
        const color = feature.properties.color || '#000000';
        const oggettoInterventoNome = feature.properties.oggettoInterventoCodice || '';

        // Ottieni le coordinate del click
        const coordinates = e.lngLat;

        // Crea il contenuto del popup con i due codici affiancati
        const popupContent = `
          <div class="popup-content">
            <div style="display: flex; align-items: center; gap: 0.5rem; margin-bottom: 0.5rem;">
              ${vecchioCodiceUmvs ? `
                <div style="display: flex; align-items: center; background: #f0f0f0; padding: 0.25rem 0.5rem; border-radius: 4px; border-left: 3px solid #8f5cb7; min-width: 60px;">
                  <span style="color: #8B4513; font-weight: bold; font-size: 12px;">${vecchioCodiceUmvs}</span>
                </div>
              ` : ''}
              <div style="display: flex; align-items: center; background: #f0f0f0; padding: 0.25rem 0.5rem; border-radius: 4px; border-left: 3px solid #4285F4; min-width: 60px;">
                <span style="color: #3a6e75; font-weight: bold; font-size: 12px;">${codice}</span>
              </div>
            </div>
            <div style="padding: 10px;">
              ${feature.properties.bacinoNome ? `<p style="margin: 5px 0 0 0; font-size: 13px;"><strong>Bacino:</strong> <span style="color: ${color}">${feature.properties.bacinoNome}</span></p>` : ''}
              ${feature.properties.comuneNome ? `<p style="margin: 5px 0 0 0; font-size: 13px;"><strong>Comune:</strong> <span style="color: ${color}">${feature.properties.comuneNome}</span></p>` : ''}
              <p style="margin: 5px 0 0 0; font-size: 13px;"><strong>Tipo:</strong> <span style="color: ${color}">${oggettoInterventoNome}</span></p>
            </div>
          </div>
        `;

        // Crea popup con informazioni
        new maplibregl.Popup({
          closeButton: true,
          closeOnClick: true,
          maxWidth: '280px'
        })
        .setLngLat(coordinates)
        .setHTML(popupContent)
        .addTo(this.map as any)
        .on('open', () => {
          // Aggiungi event listener al pulsante dopo che il popup è stato creato
          setTimeout(() => {
            const btnDettagli = document.getElementById(`dettagli-scheda-${feature.properties.id}`);
            if (btnDettagli) {
              btnDettagli.addEventListener('click', () => {
                this.apriDettagliScheda(feature.properties);
              });
            }
          }, 100); // Piccolo ritardo per assicurarsi che il DOM sia aggiornato
        });
      });
    });
  }

  /**
   * Fa zoom sulle geometrie fornite
   */
  public zoomSuGeometrie(geoJSON: any): void {
    if (!this.map || !geoJSON.features || !geoJSON.features.length) return;

    try {
      // Calcola il bounding box di tutte le geometrie
      const bounds = new maplibregl.LngLatBounds();

      // Aggiungi tutte le coordinate al bounding box
      geoJSON.features.forEach((feature: any) => {
        if (!feature.geometry || !feature.geometry.coordinates) return;

        const geometry = feature.geometry;

        if (geometry.type === 'Point') {
          const coord = geometry.coordinates;
          bounds.extend([coord[0], coord[1]]);
        }
        else if (geometry.type === 'LineString' || geometry.type === 'MultiPoint') {
          geometry.coordinates.forEach((coord: number[]) => {
            bounds.extend([coord[0], coord[1]]);
          });
        }
        else if (geometry.type === 'Polygon' || geometry.type === 'MultiLineString') {
          geometry.coordinates.forEach((line: number[][]) => {
            line.forEach((coord: number[]) => {
              bounds.extend([coord[0], coord[1]]);
            });
          });
        }
        else if (geometry.type === 'MultiPolygon') {
          geometry.coordinates.forEach((polygon: number[][][]) => {
            polygon.forEach((line: number[][]) => {
              line.forEach((coord: number[]) => {
                bounds.extend([coord[0], coord[1]]);
              });
            });
          });
        }
      });

      // Se il bounds non è vuoto, zooma su di esso
      if (!bounds.isEmpty()) {
        (this.map as any).fitBounds(bounds, {
          padding: 50,
          maxZoom: 16.5
        });
      }
    } catch (error) {
      this.logger.error('[MAPPA-SCHEDA] Errore nel calcolo del bounding box:', error);
    }
  }

  /**
   * Converte le coordinate da UTM a WGS84 (lon/lat) che MapLibre può visualizzare
   */
  private convertCoordinates(geometry: any): void {
    if (!geometry || !geometry.type) return;

    // Per Point
    if (geometry.type === 'Point' && Array.isArray(geometry.coordinates)) {
      geometry.coordinates = this.transformToWGS84(geometry.coordinates);
    }

    // Per LineString e MultiPoint
    else if ((geometry.type === 'LineString' || geometry.type === 'MultiPoint') &&
            Array.isArray(geometry.coordinates)) {
      geometry.coordinates = geometry.coordinates.map((coord: number[]) =>
        this.transformToWGS84(coord)
      );
    }

    // Per Polygon e MultiLineString
    else if ((geometry.type === 'Polygon' || geometry.type === 'MultiLineString') &&
            Array.isArray(geometry.coordinates)) {
      geometry.coordinates = geometry.coordinates.map((line: number[][]) =>
        line.map((coord: number[]) => this.transformToWGS84(coord))
      );
    }

    // Per MultiPolygon
    else if (geometry.type === 'MultiPolygon' && Array.isArray(geometry.coordinates)) {
      geometry.coordinates = geometry.coordinates.map((polygon: number[][][]) =>
        polygon.map((line: number[][]) =>
          line.map((coord: number[]) => this.transformToWGS84(coord))
        )
      );
    }

    // Per GeometryCollection
    else if (geometry.type === 'GeometryCollection' && Array.isArray(geometry.geometries)) {
      geometry.geometries.forEach((geom: any) => this.convertCoordinates(geom));
    }
  }

  /**
   * Trasforma una singola coordinata da UTM a WGS84
   */
  private transformToWGS84(coord: number[]): number[] {
    if (!coord || coord.length < 2) return coord;

    try {
      // Da EPSG:32632 (UTM Zona 32N) a WGS84 (EPSG:4326)
      const wgs84Coord = proj4('EPSG:32632', 'EPSG:4326', [coord[0], coord[1]]);
      return wgs84Coord;
    } catch (error) {
      console.error('Errore durante la conversione delle coordinate:', error);
      return coord; // Restituisci le coordinate originali in caso di errore
    }
  }

  /**
   * Crea un'immagine a forma di triangolo da utilizzare come marker
   */
  private creaTriangoloMarker(): void {
    if (!this.map) return;

    // Dimensioni dell'immagine
    const size = 24;

    // Crea un elemento canvas
    const canvas = document.createElement('canvas');
    canvas.width = size;
    canvas.height = size;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    // Pulisci il canvas
    ctx.clearRect(0, 0, size, size);

    // Disegna il triangolo
    ctx.beginPath();
    ctx.moveTo(size/2, 0);          // Vertice in alto
    ctx.lineTo(size, size);         // Vertice in basso a destra
    ctx.lineTo(0, size);            // Vertice in basso a sinistra
    ctx.closePath();

    // Riempimento verde (#33a02c)
    ctx.fillStyle = '#33a02c';
    ctx.fill();

    // Bordo bianco
    ctx.strokeStyle = 'white';
    ctx.lineWidth = 2;
    ctx.stroke();

    // Converti in immagine e aggiungi alla mappa
    const imageData = canvas.toDataURL();
    const img = new Image();
    img.onload = () => {
      (this.map as any).addImage('triangle-marker', img, { sdf: false });
    };
    img.src = imageData;
  }

  /**
   * Crea un'immagine a forma di asterisco da utilizzare come marker
   */
  private creaAsteriskMarker(): void {
    if (!this.map) return;

    // Dimensioni dell'immagine
    const size = 24;
    const centerX = size / 2;
    const centerY = size / 2;
    const radius = size / 2 - 2; // Lascia un po' di margine

    // Crea un elemento canvas
    const canvas = document.createElement('canvas');
    canvas.width = size;
    canvas.height = size;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    // Pulisci il canvas
    ctx.clearRect(0, 0, size, size);

    // Imposta lo stile per l'asterisco
    // Usa il bianco per l'immagine SDF che verrà colorata dinamicamente
    ctx.lineWidth = 3;
    ctx.strokeStyle = 'white';

    // Disegna le linee dell'asterisco (6 linee per creare un asterisco a 6 punte)
    const angles = 6;
    for (let i = 0; i < angles; i++) {
      const angle = (Math.PI / angles) * i;

      ctx.beginPath();
      // Linea dal centro verso l'esterno
      ctx.moveTo(centerX, centerY);
      ctx.lineTo(
        centerX + radius * Math.cos(angle),
        centerY + radius * Math.sin(angle)
      );
      ctx.stroke();
    }

    // Aggiungi un cerchio al centro per migliorare l'aspetto
    ctx.beginPath();
    ctx.arc(centerX, centerY, 2, 0, Math.PI * 2);
    ctx.fillStyle = 'white';
    ctx.fill();

    // Converti in immagine e aggiungi alla mappa come SDF
    const imageData = canvas.toDataURL();
    const img = new Image();
    img.onload = () => {
      // Imposta sdf: true per permettere la colorazione
      (this.map as any).addImage('asterisk-marker', img, { sdf: true });
    };
    img.src = imageData;
  }

  /**
   * Crea un pattern di linee diagonali parallele e lo aggiunge alla mappa
   */
  private creaPatternLineeDiagonali(): void {
    if (!this.map) return;

    // Dimensioni del pattern
    const size = 10;
    const lineWidth = 1;
    const spacing = 4;

    // Crea un elemento canvas
    const canvas = document.createElement('canvas');
    canvas.width = size;
    canvas.height = size;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    // Pulisci il canvas
    ctx.clearRect(0, 0, size, size);

    // Imposta il colore di sfondo trasparente
    ctx.fillStyle = 'rgba(0, 0, 0, 0)';
    ctx.fillRect(0, 0, size, size);

    // Imposta lo stile della linea
    ctx.strokeStyle = 'rgba(128, 128, 128, 0.5)'; // Grigio semi-trasparente
    ctx.lineWidth = lineWidth;

    // Disegna linee diagonali parallele
    for (let i = -size; i < size * 2; i += spacing) {
      ctx.beginPath();
      ctx.moveTo(i, 0);
      ctx.lineTo(i + size, size);
      ctx.stroke();
    }

    // Converti il canvas in un'immagine e aggiungila alla mappa
    const imageData = canvas.toDataURL();
    const img = new Image();
    img.onload = () => {
      // Quando l'immagine è caricata, aggiungila alla mappa
      (this.map as any).addImage('diagonal-stripes', img, { sdf: false });
    };
    img.src = imageData;
  }

  /**
   * Apre i dettagli di una scheda
   */
  private apriDettagliScheda(properties: any): void {
    // Ottieni l'ID della scheda
    const schedaId = properties.id;

    // this.logger.info(`[MAPPA-SCHEDA] Apertura dettagli della scheda ${schedaId}`);

    // Mostra un messaggio di conferma
    this.messageService.add({
      severity: 'info',
      summary: 'Dettagli scheda',
      detail: `Apertura dettagli della scheda "${properties.titolo}"`,
      life: 3000
    });

    // Emetti un evento per informare il componente padre
    // Qui si potrebbe implementare un'ulteriore logica per gestire i dettagli
  }

  /**
   * Esegue lo zoom sulla scheda selezionata
   */
  zoomSuScheda(): void {
    if (this.datiGeoJSON) {
      this.opzioniMappa.zoomScheda = true;
      this.zoomSuGeometrie(this.datiGeoJSON);
    }
  }

  /**
   * Attiva/disattiva la visualizzazione dei confini comunali
   */
  toggleConfiniComuni(): void {
    this.opzioniMappa.mostraComuni = !this.opzioniMappa.mostraComuni;

    if (this.map) {
      const layerId = 'comuni-confini';

      if (this.opzioniMappa.mostraComuni) {
        // Se il layer esiste già, mostralo
        if ((this.map as any).getLayer(layerId)) {
          (this.map as any).setLayoutProperty(layerId, 'visibility', 'visible');
        } else if (this.comuniData) {
          // Altrimenti, crea il layer se abbiamo i dati
          this.aggiungiConfiniComuni();
        }
      } else {
        // Nascondi il layer se esiste
        if ((this.map as any).getLayer(layerId)) {
          (this.map as any).setLayoutProperty(layerId, 'visibility', 'none');
        }
      }
    }
  }

  ngOnDestroy(): void {
    // Pulisci i marker
    this.markers.forEach(marker => marker.remove());

    // Rimuovi la mappa
    if (this.map) {
      this.map.remove();
      this.map = null;
    }
  }
}
